import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb, { getCategoryBreadcrumb } from '@/components/feature/Breadcrumb';
import { articles, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || categories[0].slug);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeTab) || categories[0],
    [activeTab],
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeTab),
    [activeTab],
  );

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-20 pb-16 md:pb-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={getCategoryBreadcrumb(currentCategory.name, currentCategory.slug)}
              />
            </div>

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                {currentCategory.name}
              </h1>
              <p className="text-foreground-600 text-sm">{currentCategory.description}</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveTab(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.imageAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2.5">
                      <span className="text-xs text-foreground-500">
                        {article.readTime} 分钟阅读
                      </span>
                      <span className="text-foreground-400">·</span>
                      <span className="text-xs text-foreground-500">{article.publishDate}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 mb-2 leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-background-200">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-background-100 text-foreground-600 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}